import { AnnStore } from '@/front/stores/ann_store.js'
import { PlayerStore } from '@/front/stores/player_store.js'
import { PlaybackStore } from '@/front/stores/playback_store.js'
import { VideoStore } from '@/front/stores/video_store.js'
import { ClickStore } from '@/front/stores/click_store.js'
import { AudioStore } from '@/front/stores/audio_store.js'
import { AssetStore } from '@/front/stores/asset_store.js'
import { ClipStore } from '@/front/stores/clip_store.js'
import { LeadCaptureStore } from '@/front/stores/lead_capture_store.js'
import { useRouter } from 'vue-router'
import { BrowserHelper } from '@/front/composables/BrowserHelper.js'
import { fixUrl } from '@/front/composables/StringComposer'
import { focus } from '@/front/composables/UxComposer'
import { VideoService } from '@/front/services'
import { MindscriptService } from '@/front/services'
import { MessageComposer } from '@/front/composables/MessageComposer'
import { preloadManager } from '@/front/services/PreloadManager.js'
const { emitMessage } = MessageComposer()
import { formatLink, objectToQueryString } from '@/front/composables/StringComposer'


const { browserInfo } = BrowserHelper()

// Store initialization helper
function getStores() {
  return {
    annStore: AnnStore(),
    assetStore: AssetStore(),
    playerStore: PlayerStore(),
    playbackStore: PlaybackStore(),
    videoStore: VideoStore(),
    clickStore: ClickStore(),
    audioStore: AudioStore(),
    leadCaptureStore: LeadCaptureStore(),
    router: useRouter()
  }
}

/**
 * Track a click interaction
 */
export function trackClick(ann) {
  const { playbackStore, videoStore } = getStores()
  playbackStore.safeTrackViewerClick(videoStore.video, ann)
}

/**
 * Evaluate and set a variable
 */
export function doVariableEvaluation(variable, value) {
  const { playbackStore } = getStores()
  log.info(3,"🔎 ClickComposer doVariableEvaluation", variable, value)
  if (variable && value) {
    if (value.toString().startsWith("{{") && value.toString().endsWith("}}")) {
      const result = MindscriptService.evaluateExpression(value, playbackStore)
      log.info(3,"🔎 ClickComposer doVariableEvaluation result", variable, value, result)
      playbackStore.setVariable(variable, result)
    } else {
      playbackStore.setVariable(variable, value)
    }
  }
}

/**
 * Submit an interaction response
 */
export function submitResponse(ann, response) {
  const { leadCaptureStore, playbackStore, videoStore } = getStores()
  
  leadCaptureStore.handleInteractionClick(ann.id)
  doVariableEvaluation(ann.variable, response)
  playbackStore.safeTrackResponse(videoStore.video, ann, response)
}

/**
 * Handle clicking an interaction
 */
export function clickInteraction(ann, completeAction = true) {
  const { clickStore, leadCaptureStore, annStore } = getStores()

  clickStore.clearTimedPause()
  leadCaptureStore.handleInteractionClick(ann)
  if (clickStore.captureLeadActive) {
    completeAction = false
    leadCaptureStore.pendingAnn = ann
  }
  trackClick(ann)
  
  ann.clicked = true

  doVariableEvaluation(ann.variable, ann.value)

  if (completeAction) {
    try {
      doAction(ann)
    } catch (error) {
      log.info(1,"Error in doAction", error)
    }
  } else {
    annStore.pausedInteraction = null
  }

  annStore.resetMustClickInteractions()
  annStore.resetActive(null, "doAction")
}

/**
 * Perform the action associated with an interaction
 */
export function doAction(ann) {
  log.info(3,"ClickComposer doAction", ann.click_action, ann.click_value, ann.transformed_click_value)
  const { clickStore } = getStores()
  const actionMap = {
    'video': () => {
      const token = (ann.transformed_click_value || ann.click_value).split("---")[1]
      doVideoBranch(token, ann.click_time)
    },
    'message': () => showMessage(ann),
    'link': () => doOpenLink(ann),
    'jump': () => doJump(ann),
    'back': () => {
      const { playbackStore } = getStores()
      log.info(1,"ClickComposer doAction back", playbackStore.refer_video)
      doVideoBranch(playbackStore.refer_video, playbackStore.refer_time)
    },
    'back_auto': () => {
      const { playbackStore } = getStores()
      doVideoBranch(playbackStore.refer_video, ann.click_time)
    },
    'view': () => doImage(ann.transformed_click_value || ann.click_value || ann.transformed_content || ann.content),
    'copy': () => doCopy(ann),
    'article': () => doArticle(ann),
    'genie': () => doGenie(ann),
    'openai': () => doChatGPT(ann),
    'email': () => doEmail(ann),
    'phone': () => doPhone(ann),
    'modal': () => startLink(ann.transformed_click_value || ann.click_value),
    'collect': () => doCollect(ann),
    'download': () => doDownload(ann),
    'resourceTray': () => openMagicMenu(ann),
    'clip': () => doClip(ann),
    'audio': () => doAudio(ann),
    'track': () => doTrack(ann),
    // Below are used for end actions
    'video_auto': () => {
      const token = (ann.transformed_click_value || ann.click_value).split("---")[1]
      doVideoBranch(token, ann.click_time)
    },
    'redirect': () => {
      log.info(3,"ClickComposer doAction redirect", ann.transformed_click_value || ann.click_value)
      doRedirect(ann.transformed_click_value || ann.click_value)
    },
  }

  // For any actions that need the MM to close
  let breakingActions = ['video', 'video_auto', 'jump', 'back', 'back_auto', 'track']
  if (breakingActions.includes(ann.click_action)) {
    clickStore.hideMagicMenu()
  }



  const action = actionMap[ann.click_action]
  if (action)  { 
    action()
  } else {
    // log.info(3,"ClickComposer doAction unknown action", ann.click_action)
    doTrack(ann)
  }
}

export function doRedirect(url) {
  log.info(3,"ClickComposer doRedirect", formatLink(url))
  safeOpenLink(formatLink(url), true)
  emitMessage('redirect', { link: formatLink(url)})
}

export function submitCollect(video, answer) {
  const { clickStore, playbackStore } = getStores()
  log.info(3,"ClickComposer submitCollect", video, answer, clickStore.activeCollect)
  // clickStore.submitCollect(video, answer)
  playbackStore.safeTrackResponse(video, clickStore.activeCollect, answer).then(() => {
    finishCollect()
  })
}

export function doTrack(ann) {
  log.info(3,"ClickComposer doTrack", ann)
  const { playerStore } = getStores()
  const { annStore } = getStores()
  annStore.resetMustClickInteractions()
  playerStore.play("clickComposer doTrack")  
}

// Audio handling
export async function doAudio(ann) {
  const { playerStore, clickStore, audioStore } = getStores()
  
  playerStore.pause("clickComposer doAudio")
  clickStore.startAudio()
  await audioStore.streamById(ann.transformed_click_value || ann.click_value)
}

export function finishAudio() {
  const { clickStore, annStore, playerStore } = getStores()
  clickStore.stopAudio()
  if (annStore.isEditing) {
    log.info(3,"ClickComposer finishAudio isEditing", annStore.editAnn)
    return
  }
  if (clickStore.questionActive) {
    log.info(3,"🔎 ClickComposer finishClip questionActive, finishQuestion")
    annStore.tryFinishQuestion()
    return
  }
  playerStore.togglePlayer("clickComposer finishAudio")
}

export function doCopy(ann) {
  log.info(3,"ClickComposer doCopy", ann.click_value)
  log.info(3,window.toast)
  const { playerStore } = getStores()
  navigator.clipboard.writeText(ann.transformed_click_value || ann.click_value)
  window.toast.add({ severity: 'success', summary: 'Copied to clipboard', life: 1000 });
}

// Clip handling
export async function doClip(ann) {
  const { playerStore, clickStore } = getStores()
  const clipStore = ClipStore()
  
  playerStore.pause("clickComposer doClip")
  clickStore.startClip()
  await clipStore.streamById(ann.transformed_click_value || ann.click_value)
}

export function finishClip() {
  const { clickStore, playerStore, annStore } = getStores()
  const clipStore = ClipStore()
  
  clickStore.stopClip()
  clipStore.clear()
  if (clickStore.questionActive) {
    log.info(3,"🔎 ClickComposer finishClip questionActive, finishQuestion")
    annStore.tryFinishQuestion()
    return
  }
  playerStore.play("clickComposer finishClip")
}

// Magic Menu handling
export function openMagicMenu(ann) {
  const { playerStore, clickStore, videoStore } = getStores()
  
  let shouldPause = videoStore.video.design.magic_menu.pause
  log.info(3,"🔎 ClickComposer openMagicMenu shouldPause", shouldPause)
  if (shouldPause) {
    playerStore.pause("clickComposer openMagicMenu")
  }
  clickStore.showMagicMenu(ann.transformed_click_value || ann.click_value)
}

export function finishMagicMenu() {
  const { clickStore, playerStore} = getStores()
  clickStore.hideMagicMenu()
  playerStore.play("clickComposer finishMagicMenu")
}

// Guide handling
export function openGuide(ann) {
  const { playerStore, clickStore } = getStores()
  
  playerStore.pause("clickComposer openGuide")
  clickStore.showGuide(ann.transformed_click_value || ann.click_value)
  focus("guide-search", 250)
}

export function finishGuide() {
  const { clickStore, playerStore } = getStores()
  
  clickStore.hideGuide()
  playerStore.play("clickComposer finishGuide")
}

// File handling
export function doDownload(ann) {
  const { playerStore } = getStores()

  playerStore.pause("clickComposer doDownload")
  if (browserInfo.isIOS) {
    // Fallback to click_value if download_url is not present
    // safeOpenLink(ann.download_url || ann.click_value, false)
    emitMessage('download', { link: formatLink(ann.download_url || ann.transformed_click_value || ann.click_value)})
  } else {
    downloadFile(ann.download_url || ann.transformed_click_value || ann.click_value)
  }
}

export function downloadFileFromUrl(url, filename = null) {
  if (browserInfo.isIOS) {
    const { playerStore } = getStores()
    playerStore.pause("clickComposer doDownload")
    safeOpenLink(formatLink(url), false)
  } else {
    downloadFile(formatLink(url), filename)
  }
}

// Chat and interaction handling
export function doChatGPT(ann) {
  const { playerStore, clickStore } = getStores()
  
  playerStore.pause("clickComposer doChatGPT")
  clickStore.startChatGPT()
}

export function finishChatGPT() {
  const { clickStore, playerStore } = getStores()
  
  clickStore.stopChatGPT()
  playerStore.play("clickComposer finishChatGPT")
}

export function doGenie(ann) {
  const { playerStore, clickStore } = getStores()
  
  playerStore.pause("clickComposer doGenie")
  clickStore.startGenie()
}

export function finishGenie() {
  const { clickStore, playerStore } = getStores()
  
  clickStore.stopGenie()
  playerStore.play("clickComposer finishGenie")
}

// Collection and navigation
export function doCollect(ann) {
  const { playerStore, clickStore } = getStores()
  
  playerStore.pause("clickComposer doCollect")
  clickStore.startCollect(ann)
}

export function finishCollect() {
  const { clickStore, playerStore } = getStores()
  
  clickStore.stopCollect()
  playerStore.play("clickComposer finishCollect")
}

export function doJump(ann) {
  const { clickStore, playerStore } = getStores()
  
  clickStore.resetAll()
  playerStore.seekAndPlay(Number(ann.click_value))
}

// Media handling
export function doImage(url) {
  const { playerStore, clickStore } = getStores()
  
  playerStore.pause("clickComposer doImage")
  clickStore.setActiveImageUrl(url)
}

export function finishImage() {
  const { clickStore, playerStore, annStore } = getStores()
  log.info(3,"🔎 ClickComposer finishImage")
  clickStore.clearActiveImageUrl()
  // If showing correctnesss, no action
  if (clickStore.questionActive && clickStore.questionCorrectness.visible) {
    log.info(3,"🔎 ClickComposer finishImage questionActive and questionCorrectness.visible, not playing")
    return
  } else if (clickStore.questionActive) {
    // If showing question but not correctness, try next question
    log.info(3,"🔎 ClickComposer finishImage questionActive, try next question")
    annStore.finishCurrentQuestion()
  } else {
    // If not showing question, play the video
    playerStore.play("clickComposer finishImage")
  }
}

export function doArticle(ann) {
  const { playerStore, clickStore, assetStore } = getStores()
  
  playerStore.pause("clickComposer doArticle")
  assetStore.getArticle(ann.transformed_click_value || ann.click_value).then(article => {
    clickStore.setActiveArticle(article)
  })
}

export function showMessage(ann) {
  const { playerStore, clickStore } = getStores()
  
  playerStore.pause("clickComposer showMessage")
  clickStore.setActiveMessage(ann.transformed_click_value || ann.click_value)
}

export function finishMessage() {
  const { clickStore, annStore, playerStore } = getStores()
  
  clickStore.clearActiveMessage()
  if (!clickStore.questionActive) {
    playerStore.play("clickComposer finishMessage")
    return
  } else if (!(clickStore.isForceCorrect && !clickStore.questionCorrectness.isCorrect)) {
      // Otherwise, check if we are not incorrect for a force-correct question
    annStore.tryFinishQuestion()
  }
}

export function finishArticle() {
  const { clickStore, playerStore } = getStores()
  
  clickStore.clearActiveArticle()
  playerStore.play("clickComposer finishArticle")
}

// Link handling
export function startLink(url) {
  const { clickStore, playerStore } = getStores()
  playerStore.pause("clickComposer startLink")
  clickStore.startLink(url)
}

export function finishLink() {
  const { clickStore, annStore, playerStore } = getStores()
  
  clickStore.stopLink()
  annStore.resetActive(null, "finishLink")
  playerStore.play("clickComposer finishLink")
}

export function doEmail(ann) {
  const { playerStore } = getStores()
  
  playerStore.pause("clickComposer doEmail")
  const url = "mailto:" + (ann.transformed_click_value || ann.click_value)
  safeOpenLink(url, false)
}

export function doPhone(ann) {
  const { playerStore, clickStore } = getStores()
  playerStore.pause("clickComposer doPhone")
  if (browserInfo.isMobile) {
    const url = "tel:" + (ann.transformed_click_value || ann.click_value)
    safeOpenLink(url, false)
  } else {
    let message = ann.transformed_click_value || ann.click_value
    clickStore.setActiveMessage(message)
  }
}

export function doOpenLink(ann) {
  const { playerStore } = getStores()
  const { videoStore } = getStores()
  
  let sameTab = !videoStore.video.tab_links
  playerStore.pause("clickComposer doOpenLink")
  safeOpenLink(ann.transformed_click_value || ann.click_value, sameTab)
}

export function doVideoBranch(token, time = 0) {
  log.info(1,"🔎 ClickComposer doVideoBranch", token, time)
  const { clickStore, router, playerStore, playbackStore, videoStore, annStore, leadCaptureStore } = getStores()
  // Reset lastCurrentTime to prevent end detection
  playerStore.lastCurrentTime = 0;

  // Clean up preloaded clips when changing videos
  preloadManager.reset()

  clickStore.resetAll()
  VideoService.branchToVideo(token, time,{
    router,
    player: playerStore,
    playback: playbackStore,
    video: videoStore,
    ann: annStore,
    leadCapture: leadCaptureStore
  })
}

/**
 * Safely open a URL in a new tab or current window
 */
export function safeOpenLink(url, sameTab = false) {
  if (typeof url !== 'string' || !url.trim()) {
    log.info(1,'openLink: Invalid URL provided: ' + url)
    return false
  }

  url = fixUrl(url)

  try {
    new URL(url)
  } catch (e) {
    log.info(1,'openLink: Malformed URL.')
    return false
  }

  if (sameTab) {
    window.location.href = url
    return true
  }

  const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
  if (newWindow) {
    newWindow.opener = null
    return true
  }
  
  log.info(1,'openLink: Unable to open a new tab. Pop-up blocker might be preventing this action.')
  return false
}

/**
 * Download a file from a URL
 */
export async function downloadFile(url, filename = null) {
  log.info(3,"🔎 ClickComposer downloadFile", url, filename)
  try {
    if (typeof url !== 'string' || !url.trim()) {
      throw new Error('Invalid URL provided.')
    }

    if (!filename) {
      const urlParts = url.split('/')
      filename = urlParts[urlParts.length - 1].split('?')[0]
      if (!filename) {
        throw new Error('Unable to determine filename from URL.')
      }
    }

    log.info(3,"🔎 ClickComposer getting response for url", url)
    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
    })
    log.info(3,"🔎 ClickComposer downloadFile response")
    log.info(3,response)

    if (!response.ok) {
      throw new Error(`Failed to fetch the file. Status: ${response.status}`)
    }

    const blob = await response.blob()

    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob, filename)
      return
    }

    const blobUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = filename
    document.body.appendChild(a)
    a.style.display = 'none'
    a.click()
    a.remove()
    window.URL.revokeObjectURL(blobUrl)
  } catch (error) {
    log.info(1,error)
    alert(`Failed to download the file: ${error.message}`)
    throw error
  }
}